import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import DialogActions from "@mui/material/DialogActions";
import * as XLSX from "xlsx";
import AlertBar from "./AlertBar";
import { API_URL } from "../../config/environment";
import {
  refreshIngredients,
  refreshIngredientsMetrics,
} from "../../helpers/refreshData";

const BulkUpdateIngredients = ({ open, onClose }) => {
  const [errorMessage, setErrorMessage] = useState("");
  const [errors, setErrors] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [success, setSuccess] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [jsonData, setJsonData] = useState(null);
  const [isloading, setIsLoading] = useState(false);
  const token = sessionStorage.getItem("authToken");
  // const location = useLocation();
  const navigate = useNavigate();

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const handleUpload = () => {
    // Logic to handle file upload

    // console.log("Uploading file:", selectedFile);
    // You can use Fetch API or any other method to upload the file
    setIsLoading(true);
    const file = selectedFile;
    const reader = new FileReader();

    try {
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(sheet, { raw: false });
        if (json.length <= 0) {
          setErrors(true);
          setErrorMessage("Excel sheet is empty");
          setIsLoading(false);
          return;
        }
        // --- CELL LIMIT VALIDATION STEP ---
        let validationError = "";
        const EXCEL_LIMIT = 32767;

        // Check every row and every cell
        for (let i = 0; i < json.length; i++) {
          const row = json[i];
          for (const key in row) {
            if (typeof row[key] === "string" && row[key].length > EXCEL_LIMIT) {
              validationError = `Row ${i + 1} ("${key}") exceeds the character limit (${row[key].length}/${EXCEL_LIMIT}). Please shorten this text before importing.`;
              break; 
            }
          }
          if (validationError) break;
        }
        if (validationError) {
          setErrors(true);
          setErrorMessage(validationError);
          setIsLoading(false);
          return; // Stop the upload!
        }
        // --- END VALIDATION ---
        setJsonData(json);
        const isSuccess = uploadData(json);
        if (isSuccess) {
          refreshIngredients();
          refreshIngredientsMetrics();
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      setErrors(true);
      setErrorMessage(error?.message);
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  function handleOnClose() {
    setSelectedFile(null);
    onClose();
  }

  async function uploadData(ingredient) {
    const url = `${API_URL}/ingredients/update/bulk`;
    try {
      const jsonData = JSON.stringify(ingredient);

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: jsonData,
      });

      if (response.status === 403) {
        setErrors(true);
        return setErrorMessage("You are not permitted to update ingredients");
      }

      const data = await response.json();
      if (data.status === "success") {
        setSuccess(true);
        setSuccessMessage("Ingredients updated successfully");
        return true;
      } else {
        setErrors(true);
        setErrorMessage(data.message);
        return false;
      }
    } catch (error) {
      setErrors(true);
      console.log(error);
      setErrorMessage(error?.message);
      return false;
    }
  }

  const dialogStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backdropFilter: "blur(5px)",
  };

  const closeButtonStyle = {
    position: "absolute",
    top: 0,
    right: 0,
    margin: "16px", // Add margin as needed
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        style={dialogStyle}
        maxWidth="xl"
        fullWidth={true}
      >
        {/* Close button */}
        <IconButton
          edge="end"
          color="inherit"
          onClick={handleOnClose}
          style={closeButtonStyle}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle>
          <Typography variant="h5" gutterBottom fontWeight={700}>
            Update Ingredients
          </Typography>
          <Typography variant="h6" gutterBottom fontWeight={300} fontSize={14}>
            Select an Excel sheet to be updated to the database
          </Typography>
        </DialogTitle>
        <DialogContent>
          {/* Display current step */}

          <input
            type="file"
            id="fileInput"
            accept=".xlsx, .xls"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          <Typography
            variant="h6"
            gutterBottom
            color={"green"}
            fontWeight={300}
            fontSize={16}
          >
            {selectedFile
              ? `Selected File: ${selectedFile?.name}`
              : "No file selected"}
          </Typography>
        </DialogContent>
        <DialogActions>
          <label htmlFor="fileInput">
            <Button variant="contained" component="span">
              Select File
            </Button>
          </label>
          <Button
            variant="contained"
            //   endIcon={<AddIcon />}
            color="success"
            //   component={Link}
            onClick={handleUpload}
            disabled={!selectedFile}
            sx={{ margin: "20px" }}
          >
            Update
            {isloading && <CircularProgress size={20} color="inherit" />}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertBar
        open={errors}
        message={errorMessage}
        severity={"error"}
        onClose={() => setErrors(false)}
      />

      <AlertBar
        open={success}
        message={successMessage}
        severity={"success"}
        onClose={() => setSuccess(false)}
      />
    </>
  );
};

export default BulkUpdateIngredients;
